import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  OutlinedInput,
  Stack,
  TextField,
  useTheme
} from '@mui/material';
import { useAppSelector } from 'src/app/hooks';
import TurnedInNotIcon from '@mui/icons-material/TurnedInNot';
import * as Yup from 'yup';
import { FieldArray, useFormik } from 'formik';
import { IAutoClippingPayload } from 'src/models/onlineMedia';
import { DesktopDatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { withStyles } from '@mui/styles';
import useOnlineMedia from 'src/services/useOnlineMedia';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { TMediaType } from 'src/models/offlineMedia';
import useOfflineMedia from 'src/services/useOfflineMedia';
import { useAlert } from 'src/hooks/useAlert';
import { CustomGraphQLError } from 'src/models/general';
import { regexAllLanguagesExceptDoubleQuote } from 'src/constants';
import Information from 'src/components/Information';

const CustomAutocomplete = withStyles({
  inputRoot: {
    '&[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"] .MuiAutocomplete-input':
      {
        padding: 0
      }
  }
})((props: any) => <Autocomplete {...props} />);

function validationSchema() {
  return Yup.object({
    name: Yup.string().required(),
    startingDate: Yup.date().required(),
    phrasesList: Yup.array().of(
      Yup.array().of(
        Yup.string()
          .min(2, ' Phrase must contain at least two characters.')
          .matches(
            regexAllLanguagesExceptDoubleQuote,
            ' Phrase must not contain symbols.'
          )
      )
    ),
    excludePhrases: Yup.array().of(
      Yup.string()
        .min(2, ' Phrase must contain at least two characters.')
        .matches(
          regexAllLanguagesExceptDoubleQuote,
          ' Phrase must not contain symbols.'
        )
    )
  });
}

interface IProps {
  clippingType?: TMediaType;
  onCompletedSubmit?: () => void;
}

const ModalAddAutoClippingRetail = ({
  clippingType = 'onlineMedia',
  onCompletedSubmit = () => {}
}: IProps) => {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const theme = useTheme();
  const { handleClickAlert } = useAlert();
  const {
    id: projectId,
    createdAt,
    customerType
  } = useAppSelector((state) => state.users.project);
  const clippingList = useAppSelector(
    (state) => state.storeOnlineMediaStream.clippingList.data
  );
  const booleanQuery = useAppSelector(
    (state) => state.chatNow.copiedBooleanQuery
  );
  const { getClippingsV5, addClippingV5, responseAddClippingV5 } =
    useOnlineMedia();
  const { addClippingOfflineMedia } = useOfflineMedia();

  const isRetail = customerType === 'b2c';

  const {
    handleChange,
    setFieldValue,
    handleSubmit,
    errors,
    values,
    touched,
    resetForm,
    isSubmitting,
    setSubmitting
  } = useFormik<IAutoClippingPayload>({
    initialValues: {
      name: '',
      phrasesList: [[]],
      startingDate: new Date(),
      excludePhrases: []
    },
    validationSchema: validationSchema(),
    onSubmit: (values) => {
      setSubmitting(true);
      const formattedDate = moment(
        values.startingDate.setHours(0, 0, 0, 0)
      ).format('YYYY-MM-DD HH:mm:ss');

      if (clippingType === 'onlineMedia') {
        addClippingV5({
          variables: {
            ...values,
            projectId,
            startingDate: formattedDate
          }
        }).then((data) => {
          setSubmitting(false);
          if (data.errors) {
            const errorResponse = data.errors as CustomGraphQLError[];
            const [message] = errorResponse[0].extensions.reason.failures;
            handleClickAlert({
              message,
              horizontal: 'center',
              vertical: 'top',
              severity: 'error'
            });
            return;
          }
          handleClose();
          getClippingsV5({
            projectId,
            page: clippingList.page,
            limit: clippingList.limit,
            name: ''
          });
        });
        return;
      }

      addClippingOfflineMedia({
        variables: {
          ...values,
          projectId,
          startingDate: formattedDate,
          categoryArticle: clippingType
        },
        onCompleted: () => {
          setSubmitting(false);
          handleClose();
          onCompletedSubmit();
        }
      });
    }
  });

  const handleClickPasteBooleanQuery = () => {
    setFieldValue('phrasesList', booleanQuery.phrasesList);
    if (clippingType === 'onlineMedia')
      setFieldValue('excludePhrases', booleanQuery.excludePhrases);
  };

  const errorPhraseList = new Set(errors?.phrasesList?.[0]);
  const errorExcludePhrases = new Set(errors?.excludePhrases);

  const createdDate = moment(createdAt);

  const minDate = new Date(
    createdDate.year(),
    createdDate.month() - 1,
    createdDate.date()
  );

  return (
    <>
      <Button
        variant="contained"
        sx={{
          borderRadius: '50px',
          height: theme.spacing(5),
          minWidth: theme.spacing(6),
          px: 3,
          background: '#000000',
          color: theme.palette.common.white,
          whiteSpace: 'nowrap',
          boxShadow: 'none',
          '&.MuiButtonBase-root': {
            borderRadius: '50px'
          },
          '&:hover': {
            background: '#333333',
            boxShadow: 'none'
          }
        }}
        onClick={handleClickOpen}
        startIcon={<TurnedInNotIcon />}
      >
        Make Clipping
      </Button>

      <Dialog
        onClose={handleClose}
        open={open}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 3, pt: 2.5, pb: 0 }}
        >
          <Typography variant="h3" fontWeight={700} fontSize={20}>
            New Clipping
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        <DialogContent sx={{ px: 3, pt: 2, pb: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Typography sx={{ mb: 0.5, fontWeight: 700, fontSize: '13px' }}>
                  Clipping Name
                </Typography>
                <FormControl fullWidth size="medium">
                  <OutlinedInput
                    name="name"
                    onChange={handleChange}
                    placeholder="Type keywords related to the news you want to track"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.preventDefault();
                    }}
                  />
                  <FormHelperText error sx={{ ml: 0 }}>
                    {errors.name && touched.name && errors.name}
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    bgcolor: '#FAFAFA',
                    border: '1px solid #E0E0E0',
                    borderRadius: '12px',
                    p: 2.5
                  }}
                >
                  <Typography
                    sx={{ mb: 0.5, fontWeight: 700, fontSize: '13px' }}
                  >
                    Starting Date
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                      value={values.startingDate}
                      onChange={(newValue) => {
                        setFieldValue('startingDate', newValue);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                      minDate={isRetail ? minDate : undefined}
                    />
                  </LocalizationProvider>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 2.5, mb: 1.5 }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>
                      Phrase
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleClickPasteBooleanQuery}
                      endIcon={
                        <Information
                          color="#fff"
                          content={
                            <Typography
                              variant="body1"
                              color="#fff"
                              fontSize={12}
                            >
                              Paste Boolean Query from Chat AI into the phrase
                              field.
                            </Typography>
                          }
                        />
                      }
                      sx={{
                        borderRadius: theme.spacing(1),
                        px: 2,
                        bgcolor: theme.palette.primary.main,
                        '&:hover': { bgcolor: theme.palette.primary.dark }
                      }}
                    >
                      Paste Boolean Query
                    </Button>
                  </Stack>

                  <FieldArray name="phrasesList" validateOnChange={false}>
                    {() => (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          maxHeight: 128,
                          overflowY: 'auto',
                          overflowX: 'hidden',
                          pb: 1,
                          pr: 1
                        }}
                      >
                        {values.phrasesList.map((rows, index) => (
                          <Stack
                            key={index}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ width: 'calc(50% - 8px)' }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <CustomAutocomplete
                                multiple
                                size="small"
                                options={[]}
                                defaultValue={[]}
                                freeSolo
                                value={rows}
                                renderTags={(vals: Array<any>, getTagProps) =>
                                  vals.map((option: string, i: number) => (
                                    <Chip
                                      key={`${option}-${i}-phrases`}
                                      variant="outlined"
                                      label={<Typography>{option}</Typography>}
                                      size="small"
                                      {...getTagProps({ index: i })}
                                    />
                                  ))
                                }
                                onChange={(_e, value) => {
                                  setFieldValue(`phrasesList[${index}]`, value);
                                }}
                                forcePopupIcon={false}
                                disableClearable
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    size="small"
                                    autoComplete="off"
                                    placeholder="Separated by Enter"
                                    sx={{ bgcolor: 'white', borderRadius: 1 }}
                                    onPaste={(event) => {
                                      let keywords =
                                        event.clipboardData.getData('text');
                                      keywords = keywords.replace(
                                        /[\u201C\u201D]/g,
                                        '"'
                                      );
                                      const trimmed = keywords
                                        .split(',')
                                        .filter((w) => w.trim().length > 1)
                                        .map((w) => w.trim());
                                      setFieldValue(`phrasesList[${index}]`, [
                                        ...values.phrasesList[index],
                                        ...trimmed
                                      ]);
                                    }}
                                  />
                                )}
                              />
                            </Box>
                            <Typography
                              sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                fontSize: '13px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                              onClick={() => {
                                setFieldValue('phrasesList', [
                                  ...values.phrasesList,
                                  []
                                ]);
                              }}
                            >
                              OR
                            </Typography>
                          </Stack>
                        ))}
                      </Box>
                    )}
                  </FieldArray>
                  <FormHelperText error sx={{ ml: 0 }}>
                    {errors.phrasesList &&
                      touched.phrasesList &&
                      errorPhraseList}
                  </FormHelperText>

                  {clippingType === 'onlineMedia' && (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 2,
                          mb: 0.5,
                          fontWeight: 600,
                          fontSize: '12px'
                        }}
                      >
                        Exclude
                      </Typography>
                      <CustomAutocomplete
                        multiple
                        id="keyword-excludes"
                        size="small"
                        options={[]}
                        defaultValue={[]}
                        freeSolo
                        value={values.excludePhrases}
                        renderTags={(vals: Array<any>, getTagProps) =>
                          vals.map((option: string, index: number) => (
                            <Chip
                              key={`${option}-${index}-excludes`}
                              variant="outlined"
                              label={<Typography>{option}</Typography>}
                              size="small"
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                        onChange={(_e, value) => {
                          setFieldValue('excludePhrases', value);
                        }}
                        forcePopupIcon={false}
                        disableClearable
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="excludes"
                            variant="outlined"
                            size="small"
                            autoComplete="off"
                            placeholder="Press Enter to Separate"
                            sx={{ bgcolor: 'white', borderRadius: 1 }}
                            onPaste={(event) => {
                              const keywords =
                                event.clipboardData.getData('text');
                              const trimmed = keywords
                                .split(',')
                                .filter((w) => w.trim().length > 1)
                                .map((w) => w.trim());
                              setFieldValue('excludePhrases', [
                                ...values.excludePhrases,
                                ...trimmed
                              ]);
                            }}
                          />
                        )}
                      />
                      <FormHelperText error sx={{ ml: 0 }}>
                        {errors.excludePhrases &&
                          touched.excludePhrases &&
                          errorExcludePhrases}
                      </FormHelperText>
                    </>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="center"
                  gap={2}
                  sx={{ mt: 1 }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{
                      borderRadius: '50px',
                      px: 5,
                      py: 1.2,
                      minWidth: 130,
                      fontSize: '15px',
                      fontWeight: 600
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={responseAddClippingV5.loading || isSubmitting}
                    sx={{
                      borderRadius: '50px',
                      px: 5,
                      py: 1.2,
                      minWidth: 130,
                      fontSize: '15px',
                      fontWeight: 600,
                      bgcolor: theme.palette.primary.main,
                      '&:hover': { bgcolor: theme.palette.primary.dark }
                    }}
                  >
                    {isSubmitting ? <CircularProgress size={20} /> : 'Save'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModalAddAutoClippingRetail;
